// Ad-conversion tracking: Google Ads (gtag.js) + Meta Pixel, wired for the 8-event
// funnel spec (landing_viewed -> ... -> subscription_converted/cancelled — the
// last 3 are server-side only, see backend/tracking.py + backend/billing.py).
//
// Both vendors are OPT-IN via env vars — if a vendor's ID isn't set, its script
// never loads and every call below silently no-ops. This lets the wiring ship
// now and go live the moment REACT_APP_GOOGLE_ADS_CONVERSION_ID /
// REACT_APP_META_PIXEL_ID are supplied, with zero code change.
//
// prefers-reduced-motion has no bearing here; this file has no UI.

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID; // e.g. G-XXXXXXX (GA4) — optional, separate from Ads conversion id
const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID; // e.g. AW-XXXXXXXXX
const GOOGLE_ADS_LABELS = {
  // Conversion Action labels, created in the Google Ads UI (Goals > Conversions).
  // Fill these in once the actions exist — client-side gtag conversions need a
  // label per action, unlike Meta which just needs an event name.
  signupCompleted: process.env.REACT_APP_GADS_LABEL_SIGNUP || null,
  checkoutStarted: process.env.REACT_APP_GADS_LABEL_CHECKOUT || null,
};
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

let gtagReady = false;
let fbqReady = false;

function loadScript(src) {
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.async = true;
    s.src = src;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
}

// Captures gclid/fbclid from the URL at first landing touch (before any
// redirect strips them) and Meta's own _fbp/_fbc cookies, storing everything
// in localStorage so it survives across the register()/checkout hops and can
// be replayed by the backend when the REAL (webhook-confirmed) conversion
// happens days later.
function captureClickIds() {
  try {
    const params = new URLSearchParams(window.location.search);
    const gclid = params.get("gclid");
    const fbclid = params.get("fbclid");
    if (gclid) localStorage.setItem("td_gclid", gclid);
    if (fbclid) localStorage.setItem("td_fbclid", fbclid);
  } catch { /* localStorage unavailable (private mode) — attribution is best-effort */ }
}

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

// What the backend needs at registration time to later report server-side
// conversions back to the right ad platform/click. Call this when building the
// POST /auth/register payload.
export function getStoredClickIds() {
  let gclid = null, fbclid = null;
  try {
    gclid = localStorage.getItem("td_gclid");
    fbclid = localStorage.getItem("td_fbclid");
  } catch { /* ignore */ }
  return {
    gclid: gclid || undefined,
    fbclid: fbclid || undefined,
    fbp: readCookie("_fbp") || undefined,
    fbc: readCookie("_fbc") || undefined,
  };
}

// Call once, at app root. Injects both vendor scripts only if configured.
export async function initTracking() {
  captureClickIds();

  if (GOOGLE_ADS_ID || GA_ID) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    const measurementId = GOOGLE_ADS_ID || GA_ID;
    const ok = await loadScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`);
    if (ok) {
      gtagReady = true;
      if (GOOGLE_ADS_ID) window.gtag("config", GOOGLE_ADS_ID);
      if (GA_ID && GA_ID !== GOOGLE_ADS_ID) window.gtag("config", GA_ID);
    }
  }

  if (META_PIXEL_ID) {
    /* eslint-disable */
    (function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)})(window,
    document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq("init", META_PIXEL_ID);
    window.fbq("track", "PageView");
    fbqReady = true;
  }
}

// --- Named events matching the funnel spec (client-side: 1-5) ---

export function trackLandingViewed({ plan } = {}) {
  if (gtagReady) window.gtag("event", "landing_viewed", { plan });
  if (fbqReady) window.fbq("track", "PageView");
}

export function trackPlanSelected(planName, billingPeriod, value = 1) {
  if (gtagReady) window.gtag("event", "plan_selected", { plan_name: planName, billing_period: billingPeriod, value, currency: "USD" });
  if (fbqReady) window.fbq("track", "ViewContent", { content_name: planName, value, currency: "USD" });
}

export function trackSignupStarted(planName) {
  if (gtagReady) window.gtag("event", "signup_started", { plan_name: planName });
  if (fbqReady) window.fbq("trackCustom", "signup_started", { plan_name: planName });
}

export function trackSignupCompleted(planName, country) {
  if (gtagReady) {
    window.gtag("event", "signup_completed", { plan_name: planName, country });
    if (GOOGLE_ADS_LABELS.signupCompleted) {
      window.gtag("event", "conversion", { send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_LABELS.signupCompleted}` });
    }
  }
  if (fbqReady) window.fbq("track", "CompleteRegistration", { plan_name: planName, country });
}

export function trackCheckoutStarted(planName, sessionId, value = 1) {
  if (gtagReady) {
    window.gtag("event", "checkout_started", { plan_name: planName, transaction_id: sessionId, value, currency: "USD" });
    if (GOOGLE_ADS_LABELS.checkoutStarted) {
      window.gtag("event", "conversion", { send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_LABELS.checkoutStarted}`, value, currency: "USD", transaction_id: sessionId });
    }
  }
  if (fbqReady) window.fbq("track", "InitiateCheckout", { content_name: planName, value, currency: "USD" });
}
